/**
 * Kanban API — thin typed wrappers over the gateway, mirroring
 * apps/chaos-gateway/src/routes/kanban/*.
 *
 * Every method returns a parsed result (consumers don't need to JSON-
 * parse manually). On non-2xx the underlying `apiJson` throws an
 * `ApiError` which TanStack Query surfaces.
 */
import type {
  Board,
  Card,
  Column,
  CreateBoardInput,
  CreateCardInput,
  CreateColumnInput,
  MoveCardInput,
  UpdateBoardInput,
  UpdateCardInput,
  UpdateColumnInput,
} from '@chaos/types/kanban';
import { cardLiveStateSchema, type CardLiveState } from '@chaos/types';

import { apiJson } from './client';

interface BoardsListResponse {
  boards: Board[];
}
interface BoardResponse {
  board: Board;
}
interface ColumnsListResponse {
  columns: Column[];
}
interface ColumnResponse {
  column: Column;
}
interface CardsListResponse {
  cards: Card[];
}
interface CardResponse {
  card: Card;
}

export type ExactMessageDelivery =
  | { kind: 'absent' }
  | { kind: 'unknown'; reason: 'db_error' | 'contract_inconsistent' }
  | { kind: 'queued'; queueId: string; clientIdempotencyKey: string }
  | {
      kind: 'terminal';
      queueId: string;
      clientIdempotencyKey: string;
      state: 'failed' | 'cancelled';
    }
  | {
      kind: 'found';
      runId: string;
      userMessageId: string;
      clientIdempotencyKey: string;
      delivery: {
        state: 'pending' | 'delivering' | 'delivered' | 'undeliverable';
        deliveredAt: string | null;
        seq: string;
        attempts: number;
      } | null;
      deliveryUnknown: boolean;
    };

/**
 * Per-tab item counts for the card slide-over's "has items" dots. Only the
 * content-collection tabs that have a clean per-card count are included; keys
 * map 1:1 to the gateway GET /kanban/cards/:cardId/tab-counts response.
 */
export interface CardTabCounts {
  notes: number;
  subTasks: number;
  artifacts: number;
  roleMessages: number;
  members: number;
  research: number;
  calls: number;
  debate: number;
  subAgents: number;
}
interface CardTabCountsResponse {
  counts: CardTabCounts;
}

export const kanbanApi = {
  // ── Boards ────────────────────────────────────────────────────────
  listBoards(): Promise<Board[]> {
    return apiJson<BoardsListResponse>('/kanban/boards').then((r) => r.boards);
  },
  getBoard(boardId: string): Promise<Board> {
    return apiJson<BoardResponse>(`/kanban/boards/${boardId}`).then((r) => r.board);
  },
  createBoard(input: CreateBoardInput): Promise<Board> {
    return apiJson<BoardResponse>('/kanban/boards', { method: 'POST', json: input }).then(
      (r) => r.board,
    );
  },
  updateBoard(boardId: string, input: UpdateBoardInput): Promise<Board> {
    return apiJson<BoardResponse>(`/kanban/boards/${boardId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.board);
  },
  deleteBoard(boardId: string): Promise<Board> {
    return apiJson<BoardResponse>(`/kanban/boards/${boardId}`, { method: 'DELETE' }).then(
      (r) => r.board,
    );
  },

  // ── Columns ───────────────────────────────────────────────────────
  listColumns(boardId: string): Promise<Column[]> {
    return apiJson<ColumnsListResponse>(`/kanban/boards/${boardId}/columns`).then((r) => r.columns);
  },
  createColumn(boardId: string, input: CreateColumnInput): Promise<Column> {
    return apiJson<ColumnResponse>(`/kanban/boards/${boardId}/columns`, {
      method: 'POST',
      json: input,
    }).then((r) => r.column);
  },
  updateColumn(columnId: string, input: UpdateColumnInput): Promise<Column> {
    return apiJson<ColumnResponse>(`/kanban/columns/${columnId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.column);
  },
  /**
   * ARCHIVE a column (soft delete) and re-home its cards.
   *
   * Points at the BOARD-scoped route, not the legacy /kanban/columns/:id one.
   * Until 2026-07-26 this called `/kanban/columns/:id`, which ran a bare
   * `DELETE FROM chaos.columns` — unrecoverable, even though
   * `chaos.columns.archived_at` and this route both already existed. (The
   * gateway made the legacy route archive too, so a stale tab is safe, but new
   * code goes through the route that reports where the cards went.)
   *
   * `cardsMoved` / `movedTo` are what the confirm dialog promised: cards cannot
   * stay in an archived column because `cards.column_id` is NOT NULL and every
   * board reader filters archived columns out, so they move to the board's
   * leftmost column.
   */
  archiveColumn(
    boardId: string,
    columnId: string,
  ): Promise<{ cardsMoved: number; movedTo: { id: string; name: string } }> {
    return apiJson<{ cardsMoved: number; movedTo: { id: string; name: string } }>(
      `/boards/${boardId}/columns/${columnId}`,
      { method: 'DELETE' },
    );
  },

  // ── Cards ─────────────────────────────────────────────────────────
  listCards(boardId: string): Promise<Card[]> {
    return apiJson<CardsListResponse>(`/kanban/boards/${boardId}/cards`).then((r) => r.cards);
  },
  getCard(cardId: string): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}`).then((r) => r.card);
  },
  /**
   * One freshly-computed founder-facing answer for every live-state reader.
   * The schema check is deliberately at the HTTP boundary: an incomplete or
   * contradictory tuple is unknown, never a collection of client defaults.
   */
  getCardLiveState(cardId: string): Promise<CardLiveState> {
    return apiJson<unknown>(`/cards/${encodeURIComponent(cardId)}/live-state`).then((body) =>
      cardLiveStateSchema.parse(body),
    );
  },
  getMessageDelivery(
    cardId: string,
    identity: {
      sessionId: string;
      clientIdempotencyKey: string;
      userMessageId?: string | null;
    },
  ): Promise<ExactMessageDelivery> {
    const query = new URLSearchParams({
      sessionId: identity.sessionId,
      clientIdempotencyKey: identity.clientIdempotencyKey,
    });
    if (identity.userMessageId) query.set('userMessageId', identity.userMessageId);
    return apiJson<ExactMessageDelivery>(
      `/cards/${encodeURIComponent(cardId)}/message-delivery?${query.toString()}`,
    );
  },
  // Lightweight per-tab item counts — one round-trip so the slide-over can
  // dot the non-empty content tabs without opening each one.
  getCardTabCounts(cardId: string): Promise<CardTabCounts> {
    return apiJson<CardTabCountsResponse>(`/kanban/cards/${cardId}/tab-counts`).then(
      (r) => r.counts,
    );
  },
  createCard(input: CreateCardInput): Promise<Card> {
    return apiJson<CardResponse>('/kanban/cards', { method: 'POST', json: input }).then(
      (r) => r.card,
    );
  },
  updateCard(cardId: string, input: UpdateCardInput): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.card);
  },
  moveCard(cardId: string, input: MoveCardInput): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}/move`, {
      method: 'POST',
      json: input,
    }).then((r) => r.card);
  },
  // Relocate a card to a DIFFERENT board (and possibly a different
  // project/org). Mirrors the gateway endpoint mounted at
  // POST /api/kanban/cards/:cardId/move-to-board. `targetColumnId` is
  // optional (gateway picks the board's first live column when omitted);
  // `position` is optional (gateway appends to the end of the column).
  moveCardToBoard(
    cardId: string,
    input: { targetBoardId: string; targetColumnId?: string; position?: number },
  ): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}/move-to-board`, {
      method: 'POST',
      json: input,
    }).then((r) => r.card);
  },
  deleteCard(cardId: string): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}`, { method: 'DELETE' }).then(
      (r) => r.card,
    );
  },
  // Reverses an archive — used by the "Undo" toast that fires
  // immediately after deleteCard. The gateway endpoint bypasses the
  // `archivedAt IS NULL` filter the other write routes apply.
  restoreCard(cardId: string): Promise<Card> {
    return apiJson<CardResponse>(`/kanban/cards/${cardId}/restore`, { method: 'POST' }).then(
      (r) => r.card,
    );
  },
};

// ── Query-key factory ───────────────────────────────────────────────
// Centralised so cache invalidations and prefetches use the same keys.
export const kanbanKeys = {
  all: ['kanban'] as const,
  boards: () => [...kanbanKeys.all, 'boards'] as const,
  board: (boardId: string) => [...kanbanKeys.all, 'board', boardId] as const,
  boardColumns: (boardId: string) => [...kanbanKeys.all, 'board', boardId, 'columns'] as const,
  boardCards: (boardId: string) => [...kanbanKeys.all, 'board', boardId, 'cards'] as const,
  card: (cardId: string) => [...kanbanKeys.all, 'card', cardId] as const,
  cardLiveState: (cardId: string) =>
    [...kanbanKeys.all, 'card', cardId, 'live-state'] as const,
  cardTabCounts: (cardId: string) =>
    [...kanbanKeys.all, 'card', cardId, 'tab-counts'] as const,
};
